// Shared input formatters + validators for the intake identifier fields
// (DOB / SSN / ZIP / DL number). Used by the lookup form (PersonEntryTable)
// and the "Add now" modal (IdentifiersModal) so behavior is consistent
// across both surfaces.

/** Format DOB as YYYY-MM-DD. Strips non-digits, inserts dashes after
 *  positions 4 and 6. Smart-paste: if input looks like MM/DD/YYYY (or
 *  MM-DD-YYYY, with 2- or 4-digit year), reorder to ISO before
 *  formatting — handles the common copy-paste case from court docs. */
export function formatDob(input: string): string {
  const slash = input.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (slash) {
    const mm = slash[1]!.padStart(2, "0");
    const dd = slash[2]!.padStart(2, "0");
    let yy = slash[3]!;
    if (yy.length === 2) {
      const n = parseInt(yy, 10);
      yy = String(n <= 30 ? 2000 + n : 1900 + n);
    }
    return `${yy}-${mm}-${dd}`;
  }
  const digits = input.replace(/\D/g, "").slice(0, 8);
  if (digits.length < 4) return digits;
  if (digits.length < 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
}

/** Format SSN as XXX-XX-XXXX. Strips non-digits + caps at 9. */
export function formatSsn(input: string): string {
  const digits = input.replace(/\D/g, "").slice(0, 9);
  if (digits.length < 4) return digits;
  if (digits.length < 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

/** Format ZIP — 5 digits max, strip everything else. */
export function formatZip(input: string): string {
  return input.replace(/\D/g, "").slice(0, 5);
}

/** Format NJ DL / state ID — uppercase, alphanumeric only, max 20 chars.
 *  The standard NJ format is one letter + 14 digits = 15 chars, but
 *  state-issued non-driver IDs use other formats so we don't enforce
 *  the strict shape on input — only on the validator (see below). */
export function formatDl(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 20);
}

// ─── Validators — true only when the value matches the expected shape.
// Used to gate the green ✓ on the source-coverage checklist so a typo
// can't trick the user into thinking that source will run.

export function isValidDob(s: string): boolean {
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return false;
  const y = parseInt(m[1]!, 10);
  const mo = parseInt(m[2]!, 10);
  const d = parseInt(m[3]!, 10);
  if (y < 1900 || y > 2030) return false;
  if (mo < 1 || mo > 12) return false;
  if (d < 1 || d > 31) return false;
  return true;
}

export function isValidSsn(s: string): boolean {
  return /^\d{3}-\d{2}-\d{4}$/.test(s);
}

export function isValidZip(s: string): boolean {
  return /^\d{5}$/.test(s);
}

/** NJ standard: 1 letter + 14 digits. We also accept other state-ID-shaped
 *  strings (8-20 alphanumeric chars starting with a letter) so non-driver
 *  state IDs aren't rejected — but the most common case (NJ DL) is the
 *  strict shape. */
export function isValidDl(s: string): boolean {
  return /^[A-Z][A-Z0-9]{7,19}$/.test(s);
}
